// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import { QuestionCircleOutlined, SendOutlined } from "@ant-design/icons";
import { SaveOutlined, StopOutlined } from "@ant-design/icons/lib";
import { navigate } from "@reach/router";
import { Button, Divider, Form, Modal, Popconfirm, message } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ThematicDossierWriteRequestDTO } from "../../../../api/backend-openapi";
import { requestCreateThematicDossier } from "../../../../state/thematicDossier/ThematicDossierEffects";
import { addThematicDossier } from "../../../../state/thematicDossier/ThematicDossierEvents";
import { formItemBlockLayout } from "../../../../utils/Constant";
import { displayError } from "../../../../utils/displayError";
import { validate } from "../../../../validator/ThematicDossierValidator";
import { Field } from "../../../types/NewsTypes";
import { ValidatorResult } from "../../../types/Types";
import ThematicDossierInputFormSection from "../details/ThematicDossierInputFormSection";
import ThematicDossierColorFormSection from "../details/ThematicDossierColorFormSection";

const { confirm } = Modal;

const AddThematicDossierForm: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [color, setColor] = useState<string>();

  const defaultFieldsValues: Field[] = [
    {
      name: ["publish"],
      value: "true"
    }
  ];

  const confirmationPopup = async (publish: boolean) => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("global.confirm"),
        content: publish
          ? t("thematic_dossier.publish")
          : t("thematic_dossier.save"),
        okText: t("global.yes"),
        cancelText: t("global.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    return requestCreateThematicDossier.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        addThematicDossier(result.data);
        message.success(t("add_thematic_dossier.success"));
        navigate("/thematicDossiers");
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  const handleSubmit = (
    values: { [key: string]: string | undefined | string[] },
    publish: boolean
  ) => {
    const validationResult: ValidatorResult = validate({ ...values, color });
    if (!validationResult.validate) {
      displayError(validationResult.message);
    } else {
      confirmationPopup(publish).then((confirmed: boolean) => {
        if (confirmed) {
          setButtonLoading(true);
          const thematicDossierToAdd: ThematicDossierWriteRequestDTO = {
            name: (values.name as string | undefined) || "",
            published: publish || false,
            keywordIds: (values.keywords as string[] | undefined) || [],
            color: color || "",
            description: (values.description as string | undefined) || ""
          };
          console.log("thematicDossierToAdd", thematicDossierToAdd);
          requestCreateThematicDossier(thematicDossierToAdd);
          setButtonLoading(true);
        }
      });
    }
  };

  const handleSave = (values: any) => {
    handleSubmit(values, false);
  };

  const handlePublish = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values, true);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };

  const onFinishFailed = ({ errorFields }: any) => {
    displayError("global.on_finish_failed");
    form.scrollToField(errorFields[0].name);
  };

  return (
    <Form
      {...formItemBlockLayout}
      onFinish={handleSave}
      onFinishFailed={onFinishFailed}
      fields={defaultFieldsValues}
      form={form}
    >
      <div className="form-first-item-sticky-top-container">
        <div className="form-first-item-sticky-top-content">
          <div className="d-flex align-items-center justify-content-between flex-wrap px-3">
            <Divider orientation="left">
              <h3 className="text-secondary mb-0">
                {t("thematic_dossier.actions")}
              </h3>
            </Divider>
            <div className="my-3">
              <Popconfirm
                title={t("add_thematic_dossier.cancel_confirmation")}
                okText={t("global.yes")}
                cancelText={t("global.no")}
                onConfirm={() => {
                  navigate("/thematicDossiers");
                  form.resetFields();
                }}
                placement="top"
                icon={<QuestionCircleOutlined />}
              >
                <Button htmlType="reset" className="m-2">
                  <StopOutlined /> {t("global.cancel")}
                </Button>
              </Popconfirm>
            </div>
            <div className="my-3">
              <Button
                className="btn-primary m-2"
                htmlType="submit"
                loading={buttonLoading}
              >
                <SaveOutlined /> {t("global.save")}
              </Button>
              <Button
                className="btn-success m-2"
                onClick={handlePublish}
                loading={buttonLoading}
              >
                <SendOutlined /> {t("global.publish")}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Divider orientation="left">
        <h3 className="text-secondary mb-0">{t("thematic_dossier.infos")}</h3>
      </Divider>
      <ThematicDossierInputFormSection createMode={true} />
      <ThematicDossierColorFormSection
        color={color}
        setColor={setColor}
        editMode={true}
      />
    </Form>
  );
};

export default AddThematicDossierForm;
